/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component, Prop, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {
    IssueInterface,
    IssuesStateInterface,
    VoteConfigInterface, VoteDeleteConfigInterface,
    VoteInterface,
} from '@/store/modules/issues/types';
import pkce from '@/services/pkce';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import Loading from '@/components/Loading/scripts';
import {AxiosError} from 'axios';
import {GameInterface} from '@/store/modules/games/types';

const namespace: string = 'issuesStateModule';

/**
 * Find an issue component.
 *
 *  * @author jgeorgiadou@frontier.co.uk
 */
@Component({
    name: 'c-issue-vote',
    components: {
        'c-loading': Loading,
    },
})
export default class IssueVote extends Vue {
    // Vote
    @Action('voteIssue', {namespace})
    public voteIssue!: (voteDataConfig: VoteInterface) => Promise<void>;
    @Action('deleteVote', {namespace})
    public deleteVote!: (voteDataConfig: VoteInterface) => Promise<void>;
    @Getter('getIssuesState', {namespace})
    public getIssuesState!: IssuesStateInterface;

    // User Details
    @Getter('authenticated', {namespace: 'userSessionModule'})
    public authenticated!: boolean;

    public state: LoadingStateInterface = new LoadingState(false, false);
    public isReadOnly: boolean = false;

    @Getter('getGames', {namespace: 'gamesStateModule'}) public getGames!: GameInterface[];

    @Prop(Object) private issue!: IssueInterface;
    @Prop(String) private stateName!: string;

    private currentRoute: string = '';

    public mounted(): void {
        const game: GameInterface = this.getGames.find((gameItem: GameInterface): boolean => {
            return this.issue.game === gameItem.name;
        }) as GameInterface;

        for (const eachGame of this.getGames) {
            if (typeof game !== 'undefined' && game.name === eachGame.name && eachGame.readonly === true) {
                this.isReadOnly = true;
            }
        }
        if (this.$router.currentRoute.name) {
            this.currentRoute = this.$router.currentRoute.name;
        }
    }

    /**
     * Vote for issue
     */
    public async vote(): Promise<void> {
        // Vote issue
        if (null === this.issue.vote && undefined !== this.issue.vote) {
            this.state.loading = true;
            const voteDataConfig: VoteConfigInterface = {
                voteData: {issue: '/api/issues/' + this.issue.id},
                issueId: this.issue.id,
                stateName: this.stateName,
                vote: null,
            };
            await this.voteIssue(voteDataConfig).catch((error: AxiosError) => {
                // Error Message
               // @ts-ignore
                422 === error['status']
                    ? this.$toastr.error(this.$i18n.translate('actions.issues.vote.error.limit.message', {}) || '')
                    : this.$toastr.error(this.$i18n.translate('actions.issues.vote.error.message', {}) || '');
            });
            this.state.loading = false;
        }
    }

    /**
     * Delete vote for this issue
     */
    public async deleteMyVote(): Promise<void> {
        // Delete vote from issue
        if (undefined !== this.issue.vote && null !== this.issue.vote) {
            this.state.loading = true;

            let voteId: any = this.issue.vote.id;

            if (typeof(this.issue.vote.id) === 'number') {
                voteId = '/api/customer_votes/' + voteId;
            }

            const voteDataConfig: VoteDeleteConfigInterface = {
                voteId,
                issueId: this.issue.id,
                stateName: this.stateName,
                vote: null,
            };

            await this.deleteVote(voteDataConfig).catch((error: AxiosError) => {
                // Error Message
                this.$toastr.error(this.$i18n.translate('actions.issues.deleteVote.error.message', {}) || '');
            });
            this.state.loading = false;
        }
    }

    /**
     * Login action event
     */
    public mustLoginToVote(): void {
        window.location.href = pkce.getAuthUrl();
    }
}
